import React from "react";
import PropTypes from "prop-types";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import { TextField, MenuItem, Chip, Checkbox, Box } from "@mui/material";
import { emphasize } from "@mui/material/styles";
import { styled } from "@mui/system";
import { CustomCheckbox } from "../../Checkbox/CustomCheckbox";

const StyledSelectInput = styled(TextField, {
  shouldForwardProp: prop => prop !== "selectProps"
})(({ theme, disabled, selectProps }) => ({
  "& .MuiFilledInput-root": {
    display: "flex",
    padding: 0,
    minHeight: "56px",
    height: "auto",
    background: disabled ? "#F2F2F2" : "#FFF",
    borderRadius: "10px",
    borderWidth: "1px",
    borderColor: "#BCBDC0",
    ...(selectProps.value?.length > 0 && selectProps.multipleChipWithLabel
      ? {
          "& .MuiFilledInput-input > div": {
            margin: "20px 0 5px"
          }
        }
      : {}),
    "& > *": {
      minHeight: "56px"
    },
    "& .MuiChip-root": {
      transform: "translateY(10px)",
      paddingY: 0
    },
    "& + fieldset": {
      borderRadius: "8px ",
      border: "solid 1px rgba(194, 194, 194, 0.56)"
    },
    "&.Mui-disabled": {
      background: "#F2F2F2",
      color: "#9B9CA0"
    }
  }
}));

const StyledDropdownIndicator = styled("div")(({ theme, disabled }) => ({
  opacity: disabled ? "0.5" : "1",
  width: "56px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  "& svg": {
    fontSize: "16px"
  }
}));

const StyledIndicatorSeparator = styled("span")(({ theme }) => ({
  width: 0,
  height: "100%",
  position: "absolute",
  top: 0,
  backgroundColor: "#ddd",
  left: 0
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  "&.MuiMenuItem-root": {
    minHeight: 48,
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    whiteSpace: "normal",
    lineHeight: "normal",
    wordBreak: "break-word",
    paddingLeft: "16px",
    paddingRight: "16px",
    fontSize: "1rem",
    textAlign: "left",
    "& .MuiCheckbox-root": {
      marginLeft: "auto"
    }
  },
  "& .customMenuItem": {
    "&.MuiMenuItem-root": {
      minHeight: 70,
      borderRadius: 10
    }
  },
  "& .customSeclectCheckbox": {
    marginRight: "0"
  }
}));

const StyledChip = styled(Chip, {
  shouldForwardProp: prop => prop !== "isFocused"
})(({ theme, isFocused }) => {
  /* istanbul ignore next */
  const themeBgColor =
    theme.palette.mode === "light" ? theme.palette.grey[300] : theme.palette.grey[700];
  return {
    margin: `${theme.spacing(0.5)} ${theme.spacing(0.25)}`,
    border: "1px solid #ccc",
    fontSize: "14px",
    height: "auto",
    borderRadius: "100px",
    gap: "0.62rem",
    padding: "0px 5px",
    "& .MuiChip-label": {
      paddingLeft: "0px",
      paddingRight: "0px",
      maxWidth: "200px",
      [theme.breakpoints.up("sm")]: {
        maxWidth: "none"
      }
    },
    backgroundColor: isFocused ? emphasize(themeBgColor, 0.08) : ""
  };
});

const CustomInputComponent = React.forwardRef((props, ref) => <Box ref={ref} {...props} />);
CustomInputComponent.displayName = "CustomInputComponent";

export const Control = ({ selectProps, innerRef, children, innerProps }) => {
  return (
    <StyledSelectInput
      fullWidth
      variant="filled"
      selectProps={selectProps}
      disabled={selectProps.isDisabled}
      InputProps={{
        inputComponent: CustomInputComponent,
        disableUnderline: true,
        inputProps: {
          ref: innerRef,
          children,
          ...innerProps
        }
      }}
      {...selectProps.textFieldProps}
      value={selectProps.value}
      data-testid="control"
    />
  );
};

export const Option = ({
  selectProps,
  innerRef,
  isFocused,
  isSelected,
  innerProps,
  children,
  isMulti
}) => (
  <StyledMenuItem
    ref={innerRef}
    selected={isFocused}
    component="div"
    className={selectProps.customCheckbox ? "customMenuItem" : ""}
    style={{
      fontWeight: isSelected ? 500 : 400
    }}
    {...innerProps}
  >
    {children}
    {isMulti && !selectProps.customCheckbox && (
      <div>
        <Checkbox color="default" checked={isSelected} />
      </div>
    )}
    {isMulti && selectProps.customCheckbox && (
      <div>
        <CustomCheckbox
          color="default"
          checked={isSelected}
          classes={{ root: "customSeclectCheckbox" }}
        />
      </div>
    )}
  </StyledMenuItem>
);

export const DropdownIndicator = ({ selectProps }) => (
  <StyledDropdownIndicator disabled={selectProps.isDisabled}>
    <KeyboardArrowDownIcon />
    <IndicatorSeparator selectProps={selectProps} />
  </StyledDropdownIndicator>
);

const IndicatorSeparator = ({ selectProps }) => (
  <StyledIndicatorSeparator data-testid="indicator-separator" />
);

export const MultiValue = ({ selectProps, children, isFocused, removeProps }) => (
  <StyledChip
    variant="outlined"
    tabIndex={-1}
    label={children}
    isFocused={isFocused}
    deleteIcon={
      <CloseIcon
        data-testid="multi-value-remove"
        style={{ width: "14px", height: "14px", fill: "#757575", marginRight: "0px" }}
      />
    }
    onDelete={event => {
      removeProps.onClick();
      removeProps.onMouseDown(event);
    }}
    data-testid="multi-value"
  />
);

Control.propTypes = {
  /**
   * Props provided by react-select
   */
  selectProps: PropTypes.object,
  /**
   * Ref to the inner element
   */
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  /**
   * Child elements
   */
  children: PropTypes.node,
  /**
   * Props to be passed to the inner element
   */
  innerProps: PropTypes.object
};

Option.propTypes = {
  /**
   * Props provided by react-select
   */
  selectProps: PropTypes.object,
  /**
   * Ref to the inner element
   */
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  /**
   * Whether the option is focused
   */
  isFocused: PropTypes.bool,
  /**
   * Whether the option is selected
   */
  isSelected: PropTypes.bool,
  /**
   * Props to be passed to the inner element
   */
  innerProps: PropTypes.object,
  /**
   * Child elements
   */
  children: PropTypes.node,
  /**
   * Whether multiple selection is enabled
   */
  isMulti: PropTypes.bool
};

DropdownIndicator.propTypes = {
  /**
   * Props provided by react-select
   */
  selectProps: PropTypes.object
};

IndicatorSeparator.propTypes = {
  /**
   * Props provided by react-select
   */
  selectProps: PropTypes.object
};

MultiValue.propTypes = {
  /**
   * Props provided by react-select
   */
  selectProps: PropTypes.object,
  /**
   * Child elements
   */
  children: PropTypes.node,
  /**
   * Whether the value is focused
   */
  isFocused: PropTypes.bool,
  /**
   * Props for the remove button
   */
  removeProps: PropTypes.shape({
    onClick: PropTypes.func.isRequired,
    onMouseDown: PropTypes.func.isRequired
  })
};
